import * as React from 'react';
import { Link } from '@tanstack/react-router';
import { CheckCircle2, GitBranch } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Spinner } from '@/components/ui/spinner';
import { ErrorMessage } from '@/components/ui/error-message';
import {
  useCapturePreviewQuery,
  useCaptureSnapshotMutation,
  type CaptureSnapshotResult,
  type SnapshotType,
} from '@/lib/api/snapshots';

/**
 * Capture-snapshot confirmation modal — shared by the overview hero and
 * the Snapshots screen.
 *
 * Why a confirmation step: a capture is not a harmless click. It writes a
 * permanent, write-once row (ADR-009), notifies the engagement team AND
 * any client viewers with an active grant (`snapshot.captured` is
 * viewer-eligible), and queues a deferred AI re-evaluation that spends AI
 * budget. The modal states each of those, with live recipient counts from
 * `GET …/snapshots/capture-preview`, before the user commits.
 *
 * Two modes:
 *   - `fixedType` set (overview hero passes `'engagement'`) — no type
 *     picker; the copy describes exactly what that type freezes.
 *   - `fixedType` omitted (Snapshots screen) — a type picker, defaulting
 *     to the engagement overview.
 *
 * After a successful capture the modal switches to a result state with
 * the sequence number and a link to the Snapshots screen, instead of
 * closing silently — the old hero button gave no feedback at all.
 *
 * Visual shell (scrim, `rounded-modal`, `shadow-modal`, amber icon tile,
 * right-aligned ghost + amber buttons) is the same one the Snapshots
 * screen's original inline modal used; tokens only, nothing invented.
 */

const TYPE_LABEL: Record<SnapshotType, string> = {
  engagement: 'Engagement overview',
  pulse: 'Pulse',
  posture: 'Health synthesis',
  readiness: 'Readiness',
};

/** What each type actually freezes — kept honest per type. */
const TYPE_FREEZES: Record<SnapshotType, string[]> = {
  engagement: [
    'Overview facts: go-live date, phase, main POC, open issues, overdue deliverables, pending AI proposals',
    'Health Check posture, overall and per domain',
    'Go-Live Readiness scorecard and category totals',
    'Budget rollup and contract deliverable status',
    'Every register entry and the methodology configuration',
  ],
  pulse: ['Every register entry and the methodology configuration'],
  posture: ['Every register entry and the methodology configuration'],
  readiness: ['Every register entry and the methodology configuration'],
};

/** Module-level captures that freeze richer content for the non-overview types. */
const TYPE_HINT: Partial<Record<SnapshotType, string>> = {
  pulse:
    'For a full contract pulse, publish it from Contract Compliance. That snapshot includes the published pulse content.',
  posture:
    'For the full health and readiness report, publish it from Health & Readiness. That snapshot includes the report content.',
  readiness:
    'For the full scorecard, capture from Health & Readiness. That snapshot includes per-criterion scores and thresholds.',
};

function newIdempotencyKey(): string {
  return typeof globalThis.crypto?.randomUUID === 'function'
    ? globalThis.crypto.randomUUID()
    : `snap-${Date.now()}`;
}

function plural(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`;
}

export interface CaptureSnapshotModalProps {
  engagementId: string;
  /** Lock the snapshot type (overview hero). Omit to show the type picker. */
  fixedType?: SnapshotType;
  onClose: () => void;
  /** Fires once after a successful capture (the modal stays open on its result state). */
  onCaptured?: (result: CaptureSnapshotResult) => void;
  /** When provided, the result state offers "Generate PDF" for the new snapshot. */
  onGeneratePdf?: (snapshotId: string) => void;
}

export function CaptureSnapshotModal({
  engagementId,
  fixedType,
  onClose,
  onCaptured,
  onGeneratePdf,
}: CaptureSnapshotModalProps) {
  const capture = useCaptureSnapshotMutation(engagementId);
  const preview = useCapturePreviewQuery(engagementId, true);
  const [snapshotType, setSnapshotType] = React.useState<SnapshotType>(fixedType ?? 'engagement');
  // One key per modal open: a double-click or a retry after a network
  // blip replays the same capture instead of minting a second permanent row.
  const idempotencyKey = React.useRef(newIdempotencyKey());
  const result = capture.data;

  // Escape closes, except mid-capture (the row may already be written).
  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !capture.isPending) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, capture.isPending]);

  const onConfirm = () => {
    capture.mutate(
      {
        input: {
          snapshot_type: snapshotType,
          source_selector: { module: snapshotType === 'engagement' ? 'overview' : snapshotType },
        },
        idempotencyKey: idempotencyKey.current,
      },
      { onSuccess: (res) => onCaptured?.(res) },
    );
  };

  const notify = preview.data?.notify;
  const viewerCount = notify?.client_viewers ?? 0;

  return (
    <div
      className="fixed inset-0 z-40 flex items-center justify-center bg-scrim p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="capture-snapshot-title"
    >
      <div className="w-full max-w-lg rounded-modal bg-paper-0 p-5 shadow-modal">
        {result ? (
          // ----- Result state -----
          <>
            <div className="flex items-start gap-3">
              <span className="rounded-card bg-posture-healthy-tint p-2 text-posture-healthy">
                <CheckCircle2 className="h-5 w-5" aria-hidden="true" />
              </span>
              <div className="flex-1 space-y-2">
                <h2 id="capture-snapshot-title" className="text-h3 font-semibold text-ink-900">
                  Snapshot captured
                </h2>
                <p className="text-body-sm text-ink-700">
                  {TYPE_LABEL[result.snapshot_type]} snapshot, sequence{' '}
                  <span className="font-mono">{result.sequence_no}</span>. It is now permanent and
                  read-only.
                </p>
              </div>
            </div>
            <div className="mt-4 flex flex-wrap justify-end gap-2">
              <Button type="button" variant="ghost" onClick={onClose}>
                Close
              </Button>
              <Button asChild type="button" variant="secondary">
                <Link to="/engagements/$engagementId/snapshot" params={{ engagementId }}>
                  View in Snapshots
                </Link>
              </Button>
              {onGeneratePdf ? (
                <Button
                  type="button"
                  onClick={() => onGeneratePdf(result.id)}
                  className="bg-accent-amber-600 text-paper-0 hover:bg-accent-amber-700"
                >
                  Generate PDF
                </Button>
              ) : null}
            </div>
          </>
        ) : (
          // ----- Confirm state -----
          <>
            <div className="flex items-start gap-3">
              <span className="rounded-card bg-accent-amber-100 p-2 text-accent-amber-700">
                <GitBranch className="h-5 w-5" aria-hidden="true" />
              </span>
              <div className="min-w-0 flex-1 space-y-3">
                <h2 id="capture-snapshot-title" className="text-h3 font-semibold text-ink-900">
                  Capture snapshot
                </h2>

                {fixedType ? null : (
                  <div className="flex flex-col gap-1">
                    <Label htmlFor="snapshot-type" className="text-meta text-ink-500">
                      Snapshot type
                    </Label>
                    <select
                      id="snapshot-type"
                      value={snapshotType}
                      onChange={(e) => setSnapshotType(e.target.value as SnapshotType)}
                      disabled={capture.isPending}
                      className="rounded-card border border-ink-200 bg-paper-0 px-3 py-2 text-body-sm"
                    >
                      <option value="engagement">{TYPE_LABEL.engagement}</option>
                      <option value="pulse">{TYPE_LABEL.pulse}</option>
                      <option value="posture">{TYPE_LABEL.posture}</option>
                      <option value="readiness">{TYPE_LABEL.readiness}</option>
                    </select>
                  </div>
                )}

                <section aria-labelledby="capture-freezes">
                  <h3
                    id="capture-freezes"
                    className="text-[10px] font-semibold uppercase tracking-[0.06em] text-ink-500"
                  >
                    What gets frozen
                  </h3>
                  <ul className="mt-1 list-disc space-y-0.5 pl-4 text-body-sm text-ink-700">
                    {TYPE_FREEZES[snapshotType].map((line) => (
                      <li key={line}>{line}</li>
                    ))}
                  </ul>
                  {TYPE_HINT[snapshotType] ? (
                    <p className="mt-1.5 text-meta text-ink-500">{TYPE_HINT[snapshotType]}</p>
                  ) : null}
                  {preview.data ? (
                    <p className="mt-1.5 text-meta text-ink-500">
                      {plural(
                        preview.data.register_entry_count,
                        'register entry',
                        'register entries',
                      )}{' '}
                      right now. This will be snapshot{' '}
                      <span className="font-mono">#{preview.data.next_sequence_no}</span>.
                    </p>
                  ) : null}
                </section>

                <section aria-labelledby="capture-effects">
                  <h3
                    id="capture-effects"
                    className="text-[10px] font-semibold uppercase tracking-[0.06em] text-ink-500"
                  >
                    What happens when you capture
                  </h3>
                  <ul className="mt-1 list-disc space-y-0.5 pl-4 text-body-sm text-ink-700">
                    <li>The snapshot is permanent. It cannot be edited or deleted.</li>
                    <li>
                      {preview.isLoading ? (
                        'Checking who will be notified…'
                      ) : notify ? (
                        notify.team_members === 0 && viewerCount === 0 ? (
                          'No one is notified: this client has no team members assigned and no client viewers have active access.'
                        ) : (
                          <>
                            A notification goes to{' '}
                            {plural(notify.team_members, 'team member', 'team members')}
                            {viewerCount > 0 ? (
                              <>
                                {' '}
                                and{' '}
                                <strong className="font-semibold text-posture-at-risk">
                                  {plural(viewerCount, 'client viewer', 'client viewers')}
                                </strong>
                                .
                              </>
                            ) : (
                              '. No client viewers have active access.'
                            )}
                          </>
                        )
                      ) : (
                        'A notification goes to the engagement team and any active client viewers.'
                      )}
                    </li>
                    <li>An AI re-evaluation is queued, which uses AI budget.</li>
                  </ul>
                </section>

                {preview.error ? (
                  <p className="text-meta text-ink-500">
                    Could not load the recipient preview. You can still capture.
                  </p>
                ) : null}
                {capture.error ? <ErrorMessage error={capture.error} /> : null}
              </div>
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <Button type="button" variant="ghost" onClick={onClose} disabled={capture.isPending}>
                Cancel
              </Button>
              <Button
                type="button"
                onClick={onConfirm}
                disabled={capture.isPending}
                className="bg-accent-amber-600 text-paper-0 hover:bg-accent-amber-700"
              >
                {capture.isPending ? (
                  <Spinner />
                ) : (
                  <GitBranch className="h-4 w-4" aria-hidden="true" />
                )}
                {capture.isPending ? 'Capturing…' : 'Capture snapshot'}
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
